import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { IssuesPage } from './pages/IssuesPage';
import { NewIssuePage } from './pages/NewIssuePage';
import { IssueDetailPage } from './pages/IssueDetailPage';
import { ProfilePage } from './pages/ProfilePage';
import { PrivacyPage } from './pages/PrivacyPage';
import { BetaDisclaimerPage } from './pages/BetaDisclaimerPage';
import { AdminLayout } from './components/AdminLayout';
import { AdminCustomersPage } from './pages/AdminCustomersPage';
import { AdminCustomerDetailPage } from './pages/AdminCustomerDetailPage';
import { AdminIssuesPage } from './pages/AdminIssuesPage';
import { AdminIssueDetailPage } from './pages/AdminIssueDetailPage';
import { AdminNewIssuePage } from './pages/AdminNewIssuePage';
import { AdminProjectsPage } from './pages/AdminProjectsPage';
import { AdminProjectDetailPage } from './pages/AdminProjectDetailPage';
import { AdminAddressesPage } from './pages/AdminAddressesPage';
import { AdminProfilePage } from './pages/AdminProfilePage';
import { Layout } from './components/Layout';

export function App(): JSX.Element {
  const { loading, claims } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-gray-500">Loading…</div>
    );
  }

  if (!claims) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
        <Route path="/beta-disclaimer" element={<Layout><BetaDisclaimerPage /></Layout>} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const isStaff = claims.role === 'staff' || claims.role === 'admin';
  const home = isStaff ? '/admin/issues' : '/issues';

  return (
    <Layout>
      <Routes>
        <Route path="/login" element={<Navigate to={home} replace />} />
        <Route path="/signup" element={<Navigate to={home} replace />} />
        <Route path="/verify-email" element={<Navigate to={home} replace />} />
        <Route path="/forgot-password" element={<Navigate to={home} replace />} />
        <Route path="/reset-password" element={<Navigate to={home} replace />} />

        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/beta-disclaimer" element={<BetaDisclaimerPage />} />

        {isStaff ? (
          <>
            <Route path="/admin" element={<Navigate to="/admin/issues" replace />} />
            <Route element={<AdminLayout />}>
              <Route path="/admin/customers" element={<AdminCustomersPage />} />
              <Route path="/admin/issues" element={<AdminIssuesPage />} />
              <Route path="/admin/projects" element={<AdminProjectsPage />} />
              <Route path="/admin/addresses" element={<AdminAddressesPage />} />
              <Route path="/admin/profile" element={<AdminProfilePage />} />
            </Route>
            <Route path="/admin/customers/:sub" element={<AdminCustomerDetailPage />} />
            <Route path="/admin/issues/new" element={<AdminNewIssuePage />} />
            <Route path="/admin/issues/:issueId" element={<AdminIssueDetailPage />} />
            <Route path="/admin/projects/:pid" element={<AdminProjectDetailPage />} />
            <Route path="/issues" element={<Navigate to="/admin/issues" replace />} />
            <Route path="*" element={<Navigate to="/admin/issues" replace />} />
          </>
        ) : (
          <>
            <Route path="/issues" element={<IssuesPage />} />
            <Route path="/issues/new" element={<NewIssuePage />} />
            <Route path="/issues/:issueId" element={<IssueDetailPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="*" element={<Navigate to="/issues" replace />} />
          </>
        )}
      </Routes>
    </Layout>
  );
}
